/**
 * Site invoke — extends generated VTEX actions with site-specific server functions.
 *
 * Standard VTEX actions (cart, session, masterdata, newsletter, misc) are
 * auto-generated in invoke.gen.ts. Run `npm run generate:invoke` to update.
 */
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { forwardResponseCookies } from "@decocms/tanstack/sdk/cookiePassthrough";
import { vtexActions } from "./invoke.gen";
import {
  extractVtexCookiesFromHeader,
  stripCookieDomain,
  performVtexLogout,
  parseVtexAuthJwt,
} from "@decocms/apps-vtex/utils/authHelpers";
import { getUser } from "@decocms/apps-vtex/loaders/user";
import { getWishlist } from "@decocms/apps-vtex/loaders/wishlist";
import { addItem, removeItem } from "@decocms/apps-vtex/actions/wishlist";
import { sendEmailVerification, accessKeySignIn } from "@decocms/apps-vtex/actions/auth";
import { updateItemAttachment, updateSelectableGifts } from "@decocms/apps-vtex/actions/checkout";
import { forwardRequestContextCookies } from "./cookieBridge";
import type { Person } from "@decocms/apps-vtex/loaders/user";
import type { WishlistItem } from "@decocms/apps-vtex/loaders/wishlist";
import productsByCollectionId from "~/loaders/daniela/productsByCollectionId";
import { toGiftWrapStyles, GIFT_WRAP_COLLECTION_ID, GIFT_WRAP_ATTACHMENT_ID } from "~/utils/giftWrap";
import type { GiftWrapStyle } from "~/utils/giftWrap";
import type { OrderForm } from "./invoke.gen";

export type { OrderForm } from "./invoke.gen";

function getVtexCookies(): string {
  return extractVtexCookiesFromHeader(getRequestHeader("cookie") ?? "");
}

const _logout = createServerFn({ method: "POST" }).handler(
  async (): Promise<{ success: boolean }> => {
    const { setCookies } = await performVtexLogout(getVtexCookies());
    forwardResponseCookies(setCookies);
    return { success: true };
  },
);

const _getUserFromJwt = createServerFn({ method: "POST" }).handler(
  async (): Promise<{ email: string; userId: string } | null> => {
    return parseVtexAuthJwt(getRequestHeader("cookie") ?? "");
  },
);

const _user = createServerFn({ method: "GET" }).handler(
  async (): Promise<Person | null> => {
    return getUser(getVtexCookies());
  },
);

const _wishlist = createServerFn({ method: "GET" }).handler(
  async (): Promise<WishlistItem[]> => {
    const cookie = getVtexCookies();
    const jwt = parseVtexAuthJwt(cookie);
    if (!jwt) return [];
    return getWishlist(cookie, { shopperId: jwt.email, allRecords: true });
  },
);

function toLoaderWishlistItem(
  item: { id?: string; productId: string; sku: string; title?: string },
): WishlistItem {
  return {
    id: item.id ?? "",
    productId: item.productId,
    sku: item.sku,
    title: item.title ?? "",
  };
}

const _addToWishlist = createServerFn({ method: "POST" })
  .inputValidator((data: { productId: string; sku: string; title?: string }) => data)
  .handler(async ({ data }): Promise<WishlistItem[]> => {
    const cookie = getVtexCookies();
    const jwt = parseVtexAuthJwt(cookie);
    if (!jwt) throw new Error("User must be logged in to add to wishlist");
    const items = await addItem(data, jwt.email, cookie);
    return items.map(toLoaderWishlistItem);
  });

const _removeFromWishlist = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }): Promise<WishlistItem[]> => {
    const cookie = getVtexCookies();
    const jwt = parseVtexAuthJwt(cookie);
    if (!jwt) throw new Error("User must be logged in to remove from wishlist");
    const items = await removeItem(data.id, jwt.email, cookie);
    return items.map(toLoaderWishlistItem);
  });

const _sendLoginCode = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => data)
  .handler(async ({ data }): Promise<{ success: boolean; authenticationToken: string | null }> => {
    const result = await sendEmailVerification({ email: data.email });
    forwardRequestContextCookies();
    return result;
  });

const _verifyLoginCode = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string; code: string; authenticationToken: string }) => data)
  .handler(async ({ data }): Promise<{ success: boolean; authStatus: string }> => {
    const result = await accessKeySignIn({
      email: data.email,
      accessKey: data.code,
      authenticationToken: data.authenticationToken,
    });
    forwardRequestContextCookies();

    return { success: result.authStatus === "Success", authStatus: result.authStatus };
  });

const _giftWrapOptions = createServerFn({ method: "GET" }).handler(
  async (): Promise<GiftWrapStyle[]> => {
    const products = await productsByCollectionId({
      collectionId: GIFT_WRAP_COLLECTION_ID,
      count: 12,
    });
    return toGiftWrapStyles(products ?? []);
  },
);

// `content` é estreitado pra `{ parentSkuId: string }`: este endpoint só serve
// ao attachment de embalagem de presente, então não há motivo pra aceitar um
// payload arbitrário vindo do cliente.
const _updateItemAttachment = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { orderFormId: string; itemIndex: number; content: { parentSkuId: string } }) => data,
  )
  .handler(async ({ data }): Promise<any> => {
    const result = await updateItemAttachment({
      orderFormId: data.orderFormId,
      itemIndex: data.itemIndex,
      attachment: GIFT_WRAP_ATTACHMENT_ID,
      content: data.content,
    });
    forwardRequestContextCookies();
    return result;
  });

// Grava a escolha de um brinde selecionável (`orderForm.selectableGifts`).
// Sem isso a VTEX nunca coloca o brinde em `items` — ver ~/utils/selectableGifts.
const _updateSelectableGifts = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      orderFormId: string;
      giftId: string;
      selectedGifts: Array<{ id: string; seller: string; quantity: number }>;
    }) => data,
  )
  .handler(async ({ data }): Promise<any> => {
    const result = await updateSelectableGifts({
      orderFormId: data.orderFormId,
      giftId: data.giftId,
      selectedGifts: data.selectedGifts,
    });
    forwardRequestContextCookies();
    return result;
  });

export const invoke = {
  vtex: {
    actions: {
      ...vtexActions,
      addToWishlist: _addToWishlist,
      removeFromWishlist: _removeFromWishlist,
      updateSelectableGifts: _updateSelectableGifts as unknown as (ctx: { data: { orderFormId: string; giftId: string; selectedGifts: Array<{ id: string; seller: string; quantity: number }> } }) => Promise<OrderForm>,
      updateItemAttachment: _updateItemAttachment as unknown as (ctx: { data: { orderFormId: string; itemIndex: number; content: { parentSkuId: string } } }) => Promise<OrderForm>,
    },
    loaders: {
      user: _user,
      wishlist: _wishlist,
    },
  },
  site: {
    loaders: {
      getUserFromJwt: _getUserFromJwt,
      giftWrapOptions: _giftWrapOptions,
    },
    actions: {
      logout: _logout,
      sendLoginCode: _sendLoginCode,
      verifyLoginCode: _verifyLoginCode,
    },
  },
} as const;
